import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Save, RotateCcw, ImageIcon, ArrowLeft, Trash2, Zap, Sun, Moon, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEditorMode } from '../contexts/PageTitleContext';
import { resolveTheme, PRESETS } from '../lib/themeEngine';
import { BrandLPPreview } from '../components/brand/BrandLPPreview';
import { BrandFunnelPreview } from '../components/brand/BrandFunnelPreview';
import { getToken } from '../lib/auth';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Card Preview — ~50% card width, larger scale ─────────────
function CardPreview({ theme }) {
  const scale = 0.46;
  const w = 260, h = 240;
  return (
    <div style={{ width: Math.round(w * scale), height: Math.round(h * scale), overflow: 'hidden', borderRadius: 8, flexShrink: 0 }}>
      <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: w, height: h }}>
        <BrandLPPreview theme={theme} />
      </div>
    </div>
  );
}

const THEME_META = {
  professional: { tagline: 'Clean & Trusted', bestFor: 'Finance, Insurance, Advisory' },
  premium_dark: { tagline: 'Sleek & Elevated', bestFor: 'High-end Brands, Luxury' },
  clean_minimal: { tagline: 'Calm & Editorial', bestFor: 'Personal Brands, Creators' },
  bold_contrast: { tagline: 'Sharp & Direct', bestFor: 'Lead Gen, CTAs, Direct Response' },
  modern_vibrant: { tagline: 'Fresh & Energetic', bestFor: 'SaaS, Tech, Startups' },
  soft_neutral: { tagline: 'Warm & Welcoming', bestFor: 'Coaching, Wellness, Community' },
};

// ── Preset Card — elevation tiers ────────────────────────────
function PresetCard({ presetKey, presetDef, theme, selected, onSelect }) {
  const meta = THEME_META[presetKey] || {};
  const [hovered, setHovered] = useState(false);

  let border, bg, shadow, translate;
  if (selected) {
    border = '2.5px solid #6366f1';
    bg = 'rgba(99,102,241,0.06)';
    shadow = '0 0 0 1px rgba(99,102,241,0.4), 0 8px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.06)';
    translate = 'translateY(0)';
  } else if (hovered) {
    border = '1.5px solid rgba(255,255,255,0.10)';
    bg = 'rgba(255,255,255,0.04)';
    shadow = '0 4px 12px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.04)';
    translate = 'translateY(-1px)';
  } else {
    border = '1.5px solid rgba(255,255,255,0.06)';
    bg = 'rgba(255,255,255,0.02)';
    shadow = 'inset 0 1px 0 rgba(255,255,255,0.03)';
    translate = 'translateY(0)';
  }

  return (
    <button
      data-testid={`preset-card-${presetKey}`}
      onClick={() => onSelect(presetKey)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="text-left rounded-xl w-full"
      style={{
        border, backgroundColor: bg, boxShadow: shadow,
        transform: translate,
        transition: 'all 200ms ease-out',
        padding: selected ? '9.5px' : '10px',
      }}
    >
      <div className="flex gap-3 items-center">
        <CardPreview theme={theme} />
        <div className="flex-1 min-w-0">
          <span className="text-[15px] font-bold text-white block leading-snug">{presetDef.name}</span>
          <p className="text-[13px] text-zinc-400 mt-0.5 leading-snug">{meta.tagline}</p>
          <p className="text-xs text-zinc-300/75 mt-0.5 leading-snug">{meta.bestFor}</p>
        </div>
      </div>
    </button>
  );
}

// ── Curated Accent Palette (SaaS-grade, brand-safe) ──────────
const ACCENT_SWATCHES = [
  { hex: '#6366F1', label: 'Indigo' },
  { hex: '#3B82F6', label: 'Blue' },
  { hex: '#14B8A6', label: 'Teal' },
  { hex: '#10B981', label: 'Emerald' },
  { hex: '#8B5CF6', label: 'Purple' },
  { hex: '#F97316', label: 'Orange' },
  { hex: '#F59E0B', label: 'Amber' },
  { hex: '#EC4899', label: 'Pink' },
];

// ── Theme-aware color ordering (guidance, not restriction) ───
const THEME_COLOR_ORDER = {
  professional: ['#6366F1', '#3B82F6', '#14B8A6', '#10B981', '#8B5CF6', '#F97316', '#F59E0B', '#EC4899'],
  premium_dark: ['#6366F1', '#8B5CF6', '#3B82F6', '#14B8A6', '#EC4899', '#10B981', '#F97316', '#F59E0B'],
  clean_minimal: ['#6366F1', '#3B82F6', '#10B981', '#14B8A6', '#8B5CF6', '#F59E0B', '#F97316', '#EC4899'],
  bold_contrast: ['#6366F1', '#3B82F6', '#F97316', '#EC4899', '#8B5CF6', '#14B8A6', '#10B981', '#F59E0B'],
  modern_vibrant: ['#8B5CF6', '#6366F1', '#3B82F6', '#EC4899', '#14B8A6', '#F97316', '#10B981', '#F59E0B'],
  soft_neutral: ['#10B981', '#14B8A6', '#F59E0B', '#6366F1', '#3B82F6', '#F97316', '#8B5CF6', '#EC4899'],
};

function getOrderedSwatches(preset) {
  const order = THEME_COLOR_ORDER[preset] || THEME_COLOR_ORDER.professional;
  return order.map((hex) => ACCENT_SWATCHES.find((s) => s.hex === hex)).filter(Boolean);
}

// ── Color Swatch with hover + selection states ───────────────
function ColorSwatch({ hex, label, selected, onClick, testId }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="w-9 h-9 rounded-xl cursor-pointer"
      style={{
        backgroundColor: hex,
        border: selected ? '3px solid #fff' : hovered ? '2px solid rgba(255,255,255,0.25)' : '2px solid rgba(255,255,255,0.08)',
        transform: selected ? 'scale(1.08)' : hovered ? 'scale(1.06)' : 'scale(1)',
        boxShadow: selected
          ? `0 0 14px ${hex}60, 0 0 4px rgba(255,255,255,0.3)`
          : hovered
            ? '0 0 0 2px rgba(255,255,255,0.15)'
            : 'none',
        transition: 'all 150ms ease-out',
      }}
      title={label || hex}
    />
  );
}

export default function BrandSettingsPage() {
  const navigate = useNavigate();
  useEditorMode(true);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [exColors, setExColors] = useState([]);
  const [logoHovered, setLogoHovered] = useState(false);
  const fileRef = useRef(null);
  const [draft, setDraft] = useState({ brand_name: '', default_preset: 'professional', mode_override: null, default_anchor_color: null, brand_primary: null });

  const token = getToken();
  const hdrs = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const resolvedTheme = useMemo(() => {
    const p = draft.default_preset || 'professional';
    const pd = PRESETS[p] || PRESETS.professional;
    return resolveTheme({ preset: p, anchor_color: draft.default_anchor_color || draft.brand_primary, mode: draft.mode_override || pd.mode });
  }, [draft.default_preset, draft.mode_override, draft.default_anchor_color, draft.brand_primary]);

  const presetEntries = useMemo(() => Object.entries(PRESETS).map(([k, pd]) => ({
    key: k, presetDef: pd,
    theme: resolveTheme({ preset: k, anchor_color: draft.default_anchor_color || draft.brand_primary, mode: draft.mode_override || pd.mode }),
  })), [draft.mode_override, draft.default_anchor_color, draft.brand_primary]);

  const fetchBlob = useCallback(async (t) => {
    try { const r = await fetch(`${API}/api/brand-config/logo`, { headers: { Authorization: `Bearer ${t}` } }); return r.ok ? URL.createObjectURL(await r.blob()) : null; } catch { return null; }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/brand-config`, { headers: hdrs }); if (!r.ok) throw 0;
      const d = await r.json(); setConfig(d);
      setDraft({ brand_name: d.brand_name || '', default_preset: d.default_preset || 'professional', mode_override: d.mode_override ?? null, default_anchor_color: d.default_anchor_color || null, brand_primary: d.brand_primary || null });
      if (d.extracted_colors?.length) setExColors(d.extracted_colors);
      if (d.logo_storage_path) { const b = await fetchBlob(token); if (b) setLogoUrl(b); }
    } catch {} finally { setLoading(false); }
  }, [hdrs, token, fetchBlob]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);
  useEffect(() => () => { if (logoUrl?.startsWith('blob:')) URL.revokeObjectURL(logoUrl); }, [logoUrl]);
  useEffect(() => { if (!config) return; setDirty(draft.brand_name !== (config.brand_name || '') || draft.default_preset !== (config.default_preset || 'professional') || draft.mode_override !== (config.mode_override ?? null) || draft.default_anchor_color !== (config.default_anchor_color || null) || draft.brand_primary !== (config.brand_primary || null)); }, [draft, config]);

  const save = async () => {
    setSaving(true);
    try { const r = await fetch(`${API}/api/brand-config`, { method: 'PUT', headers: hdrs, body: JSON.stringify(draft) }); if (!r.ok) throw 0; const d = await r.json(); setConfig(d); setDraft({ brand_name: d.brand_name || '', default_preset: d.default_preset || 'professional', mode_override: d.mode_override ?? null, default_anchor_color: d.default_anchor_color || null, brand_primary: d.brand_primary || null }); setDirty(false); } catch {} finally { setSaving(false); }
  };
  const reset = () => { if (config) setDraft({ brand_name: config.brand_name || '', default_preset: config.default_preset || 'professional', mode_override: config.mode_override ?? null, default_anchor_color: config.default_anchor_color || null, brand_primary: config.brand_primary || null }); };
  const uploadLogo = async (f) => {
    if (!f) return; setUploading(true);
    try { const fd = new FormData(); fd.append('file', f); const r = await fetch(`${API}/api/brand-config/logo`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd }); if (!r.ok) throw 0; const d = await r.json(); const b = await fetchBlob(token); if (b) { if (logoUrl?.startsWith('blob:')) URL.revokeObjectURL(logoUrl); setLogoUrl(b); } if (d.extracted_colors?.length) setExColors(d.extracted_colors); } catch {} finally { setUploading(false); }
  };

  if (loading) return (
    <div data-testid="brand-settings-skeleton" className="flex flex-col h-full animate-pulse">
      {/* Action Bar skeleton */}
      <div className="h-11 mx-3 mt-2 flex-shrink-0 rounded-xl bg-white/[0.04] border border-white/[0.06]" />

      {/* Grid skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr_280px] gap-2.5 flex-1 min-h-0 px-3 pt-2.5 pb-2">
        {/* Left */}
        <div>
          <div className="rounded-xl bg-white/[0.04] border border-white/[0.06] p-5 space-y-4">
            <div className="h-3 w-28 bg-white/[0.06] rounded" />
            <div className="w-full rounded-xl bg-white/[0.03] p-5 flex items-center justify-center">
              <div className="w-[120px] h-[120px] rounded-xl bg-white/[0.04]" />
            </div>
            <div className="flex justify-center gap-2">
              <div className="h-7 w-24 rounded-lg bg-white/[0.06]" />
            </div>
            <div className="h-px bg-white/[0.06]" />
            <div className="space-y-2">
              <div className="h-3 w-20 bg-white/[0.06] rounded" />
              <div className="h-10 w-full rounded-lg bg-white/[0.04]" />
            </div>
            <div className="h-px bg-white/[0.06]" />
            <div className="space-y-2">
              <div className="h-3 w-12 bg-white/[0.06] rounded" />
              <div className="h-4 w-16 bg-white/[0.06] rounded" />
            </div>
          </div>
        </div>

        {/* Center */}
        <div className="space-y-3">
          <div className="rounded-xl bg-white/[0.04] border border-white/[0.06] p-3 space-y-2">
            <div className="h-3 w-28 bg-white/[0.06] rounded" />
            <div className="grid grid-cols-2 gap-2">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="rounded-xl bg-white/[0.03] border border-white/[0.05] p-3">
                  <div className="flex gap-3 items-center">
                    <div className="w-[120px] h-[110px] rounded-lg bg-white/[0.04]" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-24 bg-white/[0.06] rounded" />
                      <div className="h-3 w-20 bg-white/[0.04] rounded" />
                      <div className="h-3 w-32 bg-white/[0.04] rounded" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl bg-white/[0.04] border border-white/[0.06] p-4">
            <div className="grid grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="space-y-3">
                  <div className="h-3 w-20 bg-white/[0.06] rounded" />
                  <div className="h-10 w-full rounded-xl bg-white/[0.04]" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right */}
        <div>
          <div className="rounded-xl bg-white/[0.04] border border-white/[0.06] p-3 space-y-3">
            <div className="h-3 w-24 bg-white/[0.06] rounded" />
            <div className="rounded-lg bg-white/[0.03] p-2 space-y-2">
              <div className="h-3 w-20 bg-white/[0.06] rounded" />
              <div className="h-40 w-full rounded-lg bg-white/[0.04]" />
            </div>
            <div className="rounded-lg bg-white/[0.03] p-2 space-y-2">
              <div className="h-3 w-28 bg-white/[0.06] rounded" />
              <div className="h-32 w-full rounded-lg bg-white/[0.04]" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const anchor = draft.default_anchor_color || draft.brand_primary || resolvedTheme?.brand?.primary || '#2563EB';
  const pdDef = PRESETS[draft.default_preset] || PRESETS.professional;
  const eMode = draft.mode_override || pdDef.mode;
  const hasLogo = !!logoUrl;
  const hasEx = hasLogo && exColors.length > 0;
  const themeName = config?.default_theme?.name || 'Professional';

  return (
    <div data-testid="brand-settings-page" className="flex flex-col h-full">
      {/* Action Bar */}
      <div className="h-11 flex items-center justify-between px-4 mx-3 mt-2 flex-shrink-0 rounded-xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-xl">
        <div className="flex items-center gap-2">
          <button data-testid="brand-back-btn" onClick={() => navigate('/app/settings')} className="h-7 w-7 flex items-center justify-center rounded-lg hover:bg-white/[0.06]"><ArrowLeft className="h-4 w-4 text-zinc-400" /></button>
          <span className="text-sm font-semibold text-zinc-100">Brand Settings</span>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <button data-testid="brand-reset-btn" onClick={reset} className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium text-zinc-400 hover:bg-white/[0.06] border border-white/[0.08]"><RotateCcw className="w-3 h-3" /> Reset</button>}
          <button data-testid="brand-save-btn" onClick={save} disabled={!dirty || saving} className="flex items-center gap-1.5 px-4 py-1 rounded-lg text-xs font-semibold text-white disabled:opacity-40 bg-indigo-500 hover:bg-indigo-400 transition-colors"><Save className="w-3 h-3" /> {saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr_280px] gap-2.5 flex-1 min-h-0 px-3 pt-2.5 pb-2">

        {/* LEFT — Brand Identity */}
        <div className="lg:overflow-y-auto min-h-0">
          <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-xl p-5 flex flex-col">
            <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-4">Brand Identity</h3>

            {/* Logo block with hover */}
            <div className="flex flex-col items-center">
              <div
                className="w-full rounded-xl border border-white/[0.08] p-5 flex items-center justify-center cursor-pointer"
                style={{
                  background: logoHovered ? 'linear-gradient(to bottom, rgba(255,255,255,0.06), rgba(255,255,255,0.02))' : 'linear-gradient(to bottom, rgba(255,255,255,0.04), transparent)',
                  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.15)',
                  transform: logoHovered ? 'scale(1.01)' : 'scale(1)',
                  transition: 'all 200ms ease-out',
                }}
                onMouseEnter={() => setLogoHovered(true)}
                onMouseLeave={() => setLogoHovered(false)}
                onClick={() => fileRef.current?.click()}
              >
                {hasLogo ? (
                  <img src={logoUrl} alt="Logo" className="w-[120px] h-[120px] object-contain" />
                ) : (
                  <div className="w-[120px] h-[120px] rounded-xl border-2 border-dashed border-zinc-700 bg-zinc-800/20 flex flex-col items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-zinc-600 mb-1" />
                    <span className="text-[10px] text-zinc-600">No logo</span>
                  </div>
                )}
              </div>
              <p className="text-[10px] text-zinc-600 mt-2 text-center">Used across your funnels and landing pages</p>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => uploadLogo(e.target.files[0])} />
              <div className="flex items-center gap-2 mt-2.5">
                <button data-testid="logo-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium text-zinc-300 hover:bg-white/[0.06] border border-white/[0.08] transition-colors">
                  <Upload className="w-3 h-3" /> {uploading ? 'Uploading...' : hasLogo ? 'Replace Logo' : 'Upload Logo'}
                </button>
                {hasLogo && (
                  <button data-testid="logo-remove-btn" onClick={() => { setLogoUrl(null); setExColors([]); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium text-zinc-500 hover:text-red-400 hover:bg-red-500/10 border border-white/[0.08] transition-colors">
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                )}
              </div>
            </div>

            <div className="h-px bg-white/[0.08] w-full my-4" />

            {/* Brand Name */}
            <div className="w-full">
              <label className="text-[11px] text-zinc-400 mb-1.5 block font-medium">Brand Name</label>
              <input data-testid="brand-name-input" type="text" value={draft.brand_name} onChange={(e) => setDraft((d) => ({ ...d, brand_name: e.target.value }))} placeholder="Your brand name" maxLength={100}
                className="w-full bg-white/[0.05] border border-white/[0.10] rounded-lg px-3 py-2.5 text-sm text-zinc-200 focus:border-white/[0.2] focus:outline-none placeholder:text-zinc-600 transition-colors" />
            </div>

            <div className="h-px bg-white/[0.08] w-full my-4" />

            {/* Status with accent indicator */}
            <div className="w-full">
              <label className="text-[11px] text-zinc-400 mb-2 block font-medium">Status</label>
              <div className="flex items-center gap-2.5">
                <div className="w-3 h-3 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]" />
                <span className="text-sm font-semibold text-zinc-100">Active</span>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <div className="w-1 h-4 rounded-full" style={{ backgroundColor: anchor }} />
                <p className="text-[11px] text-zinc-400">Theme: {themeName}</p>
              </div>
            </div>
          </div>
        </div>

        {/* CENTER */}
        <div className="lg:overflow-y-auto min-h-0 space-y-3">
          {/* Theme Grid */}
          <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-xl p-3">
            <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Default Theme</h3>
            <div className="grid grid-cols-2 gap-2">
              {presetEntries.map(({ key, presetDef, theme }) => (
                <PresetCard key={key} presetKey={key} presetDef={presetDef} theme={theme} selected={draft.default_preset === key} onSelect={(k) => setDraft((d) => ({ ...d, default_preset: k }))} />
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-xl py-4 px-5">
            <div className="grid grid-cols-3 gap-0">
              {/* Mode */}
              <div className="pr-5 transition-all duration-150 hover:border-white/[0.14]">
                <h3 className="text-xs font-bold text-zinc-300 uppercase tracking-wider mb-3">Mode</h3>
                <div className="relative flex bg-white/[0.04] hover:bg-white/[0.06] rounded-2xl p-1 border border-white/[0.10] h-[46px] transition-colors duration-150">
                  <div
                    className="absolute top-1 bottom-1 rounded-xl transition-all duration-200 ease-in-out"
                    style={{
                      width: 'calc(33.333% - 2px)',
                      left: draft.mode_override === null ? '4px' : draft.mode_override === 'light' ? 'calc(33.333% + 1px)' : 'calc(66.666% - 2px)',
                      background: 'linear-gradient(to bottom, rgba(99,102,241,0.20), rgba(99,102,241,0.10))',
                      border: '1px solid rgba(99,102,241,0.30)',
                      boxShadow: '0 0 10px rgba(99,102,241,0.18)',
                    }}
                  />
                  {[
                    { v: null, l: 'Auto', Icon: Zap },
                    { v: 'light', l: 'Light', Icon: Sun },
                    { v: 'dark', l: 'Dark', Icon: Moon },
                  ].map(({ v, l, Icon }) => {
                    const on = draft.mode_override === v;
                    return (
                      <button key={l} data-testid={`mode-${l.toLowerCase()}`}
                        onClick={() => setDraft((d) => ({ ...d, mode_override: v }))}
                        className="relative z-10 flex-1 flex items-center justify-center gap-1.5 rounded-xl text-xs font-semibold transition-all duration-200 cursor-pointer hover:bg-white/[0.06]"
                        style={{ color: on ? '#e0e7ff' : '#71717a' }}>
                        <Icon className="w-3.5 h-3.5" />
                        {l}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-zinc-400 mt-2">
                  {draft.mode_override === null ? `Auto — uses preset default (${eMode})` : `Override: ${draft.mode_override} mode`}
                </p>
              </div>
              {/* Logo Accent Color */}
              <div className="px-5 border-l border-white/[0.10] transition-all duration-150 hover:border-white/[0.14]">
                <h3 className="text-xs font-bold text-zinc-300 uppercase tracking-wider mb-3">Logo Accent Color</h3>
                {hasEx ? (
                  <>
                    <div className="flex gap-2.5 flex-wrap">
                      {exColors.map((c, i) => {
                        const h = c.normalized_hex || c.hex;
                        return <ColorSwatch key={i} hex={h} selected={anchor === h} testId={`extracted-color-${i}`}
                          onClick={() => setDraft((d) => ({ ...d, default_anchor_color: h }))} />;
                      })}
                    </div>
                    {exColors.some((c) => anchor === (c.normalized_hex || c.hex)) && (
                      <div className="mt-2.5">
                        <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Active Color</p>
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-md" style={{ backgroundColor: anchor }} />
                          <span className="text-[11px] text-zinc-400 font-mono">{anchor}</span>
                          <div className="ml-auto px-2 py-0.5 rounded text-[9px] font-semibold" style={{ backgroundColor: anchor, color: resolvedTheme.tokens.primary_foreground }}>
                            Sample
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-[11px] text-zinc-500 italic">Upload a logo to extract</p>
                )}
              </div>
              {/* Preset Accent Color */}
              <div className="pl-5 border-l border-white/[0.10] transition-all duration-150 hover:border-white/[0.14]">
                <h3 className="text-xs font-bold text-zinc-300 uppercase tracking-wider mb-3">Preset Accent Color</h3>
                <div className="flex gap-2 flex-wrap">
                  {getOrderedSwatches(draft.default_preset).map(({ hex, label }) => (
                    <ColorSwatch key={hex} hex={hex} label={label} selected={anchor === hex} testId={`accent-swatch-${label.toLowerCase()}`}
                      onClick={() => setDraft((d) => ({ ...d, default_anchor_color: hex }))} />
                  ))}
                </div>
                {(ACCENT_SWATCHES.some(({ hex }) => anchor === hex) || !exColors.some((c) => anchor === (c.normalized_hex || c.hex))) && (
                  <div className="mt-2.5">
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Active Color</p>
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-md" style={{ backgroundColor: anchor }} />
                      <span className="text-[11px] text-zinc-400 font-mono">{anchor}</span>
                      <div className="ml-auto px-2 py-0.5 rounded text-[9px] font-semibold" style={{ backgroundColor: anchor, color: resolvedTheme.tokens.primary_foreground }}>
                        Sample
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT — Live Preview with elevation */}
        <div className="lg:overflow-y-auto min-h-0">
          <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-xl p-3"
            style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
            <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3">Live Preview</h3>
            <div className="space-y-3">
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2 transition-all duration-200">
                <p className="text-[11px] text-zinc-300 mb-2 px-0.5 font-semibold">Landing Page</p>
                <div style={{ transition: 'opacity 200ms ease-in-out' }}>
                  <BrandLPPreview theme={resolvedTheme} />
                </div>
              </div>
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2 transition-all duration-200">
                <p className="text-[11px] text-zinc-300 mb-2 px-0.5 font-semibold">Conversation Funnel</p>
                <div style={{ transition: 'opacity 200ms ease-in-out' }}>
                  <BrandFunnelPreview theme={resolvedTheme} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
